import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import Modal from "react-bootstrap/Modal";
import { DataGrid } from "@mui/x-data-grid";
import LanguageContext from "../../context/LanguageContext";
import { API_URL } from "../../components/api";
import OnlineReceiptLinksModal from "../Collection/OnlineReceiptLinksModal";
import "./OnlinePayments.css";


const createOperationId = () => {
  const bytes = new Uint8Array(16);

  if (window.crypto && window.crypto.getRandomValues) {
    window.crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }

  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(
    bytes,
    (value) => value.toString(16).padStart(2, "0")
  );

  return [
    hex.slice(0, 4).join(""),
    hex.slice(4, 6).join(""),
    hex.slice(6, 8).join(""),
    hex.slice(8, 10).join(""),
    hex.slice(10, 16).join(""),
  ].join("-");
};


const emptyCashOut = () => ({
  operation_id: createOperationId(),
  currency: "USD",
  fee: "0",
  reason: "",
});

const emptyPayment = () => ({
  operation_id: createOperationId(),
  online_account_id: "",
  amount_usd: "",
  amount_lbp: "",
  reason: "",
});


const formatMoney = (value) => {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return "0.00";
  }

  return number.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};


const errorMessage = (error, fallback) => {
  if (
    error.response &&
    error.response.data &&
    error.response.data.error
  ) {
    return error.response.data.error;
  }

  return fallback;
};


function OnlinePayments() {
  const { language, direction, t } = useContext(LanguageContext);
  const [rows, setRows] = useState([]);
  const [onlineAccounts, setOnlineAccounts] = useState([]);
  const [
    selectedOnlineAccountId,
    setSelectedOnlineAccountId,
  ] = useState("");
  const [canManageAccounts, setCanManageAccounts] = useState(false);
  const [accountHolders, setAccountHolders] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [accountForm, setAccountForm] = useState({
    account_holder_id: "",
    payment_method: "",
  });
  const [accountError, setAccountError] = useState("");
  const [addingAccount, setAddingAccount] = useState(false);
  const [activeShift, setActiveShift] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [success, setSuccess] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [rowCount, setRowCount] = useState(0);
  const [showCashOut, setShowCashOut] = useState(false);
  const [form, setForm] = useState(emptyCashOut);
  const [modalTab, setModalTab] = useState("cash_out");
  const [paymentForm, setPaymentForm] = useState(emptyPayment);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [
    linkedReceiptDetail,
    setLinkedReceiptDetail,
  ] = useState([]);

  const [
    cashOutOnlineAccountId,
    setCashOutOnlineAccountId,
  ] = useState("");
  const [
    cashOutReceipts,
    setCashOutReceipts,
  ] = useState([]);
  const [
    selectedCashOutIds,
    setSelectedCashOutIds,
  ] = useState([]);
  const [
    loadingCashOutReceipts,
    setLoadingCashOutReceipts,
  ] = useState(false);

  const loadAccountOptions = useCallback(async () => {
    try {
      const authTokens = JSON.parse(
        localStorage.getItem("authTokens")
      );

      const response = await axios.get(
        `${API_URL}/accounting/online-payment-accounts/`,
        {
          headers: {
            Authorization: `Bearer ${authTokens.access}`,
          },
        }
      );

      const data = response.data;

      setCanManageAccounts(Boolean(data.can_manage));
      setAccountHolders(
        Array.isArray(data.account_holders)
          ? data.account_holders
          : []
      );
      setPaymentMethods(
        Array.isArray(data.payment_methods)
          ? data.payment_methods
          : []
      );
    } catch (error) {
      console.error(error);
      setCanManageAccounts(false);
      setAccountHolders([]);
      setPaymentMethods([]);
    }
  }, []);

  const loadDrawer = useCallback(
    async (requestedPage = page, requestedSize = pageSize) => {
      setLoading(true);
      setPageError("");

      try {
        const authTokens = JSON.parse(
          localStorage.getItem("authTokens")
        );
        const response = await axios.get(
          `${API_URL}/accounting/online-drawer/`,
          {
            headers: {
              Authorization: `Bearer ${authTokens.access}`,
            },
            params: {
              page: requestedPage + 1,
              page_size: requestedSize,
              ...(selectedOnlineAccountId
                ? {
                    online_account_id:
                      selectedOnlineAccountId,
                  }
                : {}),
            },
          }
        );
        const data = response.data;

        setRows(Array.isArray(data.results) ? data.results : []);

        setOnlineAccounts(
          Array.isArray(data.online_accounts)
            ? data.online_accounts
            : []
        );

        if (
          data.selected_online_account_id !== null &&
          data.selected_online_account_id !== undefined
        ) {
          const nextAccountId = String(
            data.selected_online_account_id
          );

          if (
            nextAccountId !== selectedOnlineAccountId
          ) {
            setSelectedOnlineAccountId(
              nextAccountId
            );
          }
        }
        setActiveShift(Boolean(data.active_shift));
        setRowCount(data.pagination.total_rows);
      } catch (error) {
        setRows([]);
        setActiveShift(false);
        setPageError(
          errorMessage(error, t("collection.onlineLoadError"))
        );
      } finally {
        setLoading(false);
      }
    },
    [page, pageSize, selectedOnlineAccountId, t]
  );

  useEffect(() => {
    loadAccountOptions();
  }, [loadAccountOptions]);

  useEffect(() => {
    loadDrawer();
  }, [loadDrawer]);

  useEffect(() => {
    if (!success) {
      return undefined;
    }

    const timer = window.setTimeout(() => setSuccess(""), 4000);
    return () => window.clearTimeout(timer);
  }, [success]);

  const selectedCashOutTotals = useMemo(() => {
    const selected = new Set(
      selectedCashOutIds.map((value) => String(value))
    );

    return cashOutReceipts.reduce(
      (totals, row) => {
        if (
          !selected.has(
            String(row.online_drawer_transaction_id)
          )
        ) {
          return totals;
        }

        const usd = Number(
          row.remaining_usd == null
            ? row.amount_usd
            : row.remaining_usd
        );
        const lbp = Number(
          row.remaining_lbp == null
            ? row.amount_lbp
            : row.remaining_lbp
        );

        return {
          usd:
            totals.usd
            + (Number.isFinite(usd) ? usd : 0),
          lbp:
            totals.lbp
            + (Number.isFinite(lbp) ? lbp : 0),
        };
      },
      {
        usd: 0,
        lbp: 0,
      }
    );
  }, [cashOutReceipts, selectedCashOutIds]);

  const selectedAccount = useMemo(
    () =>
      onlineAccounts.find(
        (account) =>
          String(account.online_account_id) ===
          String(selectedOnlineAccountId)
      ) || null,
    [onlineAccounts, selectedOnlineAccountId]
  );

  const cashOutAccount = useMemo(
    () =>
      onlineAccounts.find(
        (account) =>
          String(account.online_account_id) ===
          String(cashOutOnlineAccountId)
      ) || null,
    [onlineAccounts, cashOutOnlineAccountId]
  );

  const columns = useMemo(() => {
    const types = {
      collection: t("collection.onlineCollectionType"),
      cash_out: t("collection.onlineCashOutType"),
      payment: t("collection.onlinePaymentType"),
      reversal: t("collection.onlineReversalType"),
    };

    const moneyColumn = (field, headerName, width) => ({
      field,
      headerName,
      width,
      valueFormatter: (params) => formatMoney(params.value),
    });

    return [
      {
        field: "creation_date",
        headerName: t("collection.onlineDate"),
        width: 185,
        valueFormatter: (params) => {
          if (!params.value) {
            return "-";
          }

          const date = new Date(params.value);
          return Number.isNaN(date.getTime())
            ? "-"
            : date.toLocaleString(
              language === "ar" ? "ar-LB" : "en-GB"
            );
        },
      },
      {
        field: "transaction_type",
        headerName: t("collection.onlineType"),
        width: 135,
        valueFormatter: (params) => (
          types[params.value] || params.value || "-"
        ),
      },
      {
        field: "payment_method",
        headerName: t("collection.onlineMethod"),
        width: 115,
        valueFormatter: (params) => (
          params.value ? String(params.value).toUpperCase() : "-"
        ),
      },
      {
        field: "client_name",
        headerName:
          language === "ar"
            ? "العميل / الموزّع"
            : "Client / Reseller",
        width: 210,
      },
      moneyColumn(
        "amount_usd",
        t("collection.onlineAmountUsd"),
        135
      ),
      moneyColumn(
        "amount_lbp",
        t("collection.onlineAmountLbp"),
        145
      ),
      moneyColumn(
        "fee_usd",
        t("collection.onlineFeeUsd"),
        120
      ),
      moneyColumn(
        "fee_lbp",
        t("collection.onlineFeeLbp"),
        130
      ),
      moneyColumn(
        "balance_change_usd",
        t("collection.onlineChangeUsd"),
        135
      ),
      moneyColumn(
        "balance_change_lbp",
        t("collection.onlineChangeLbp"),
        145
      ),
      moneyColumn(
        "balance_after_usd",
        t("collection.onlineAfterUsd"),
        175
      ),
      moneyColumn(
        "balance_after_lbp",
        t("collection.onlineAfterLbp"),
        185
      ),
      {
        field: "initiator",
        headerName:
          language === "ar"
            ? "أضيف بواسطة"
            : "Added By",
        width: 135,
      },
      {
        field: "destination_drawer",
        headerName: t("collection.onlineDestination"),
        width: 190,
      },
      {
        field: "receipt_display",
        headerName: t("collection.onlineReceipt"),
        width: 170,
        renderCell: ({ row }) => {
          const linked = Array.isArray(
            row.linked_receipts
          ) && row.linked_receipts.length > 0;

          const label =
            row.receipt_display || "-";

          if (!linked) {
            return label;
          }

          return (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();

                setLinkedReceiptDetail(
                  row.linked_receipts
                );
              }}
              style={{
                padding: 0,
                border: "none",
                background: "transparent",
                color: "inherit",
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              {label}
            </button>
          );
        },
      },
      {
        field: "invoice_display",
        headerName: t("collection.onlineInvoice"),
        width: 170,
        renderCell: ({ row }) => {
          const linked = Array.isArray(
            row.linked_receipts
          ) && row.linked_receipts.length > 0;

          const label =
            row.invoice_display || "-";

          if (!linked) {
            return label;
          }

          return (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();

                setLinkedReceiptDetail(
                  row.linked_receipts
                );
              }}
              style={{
                padding: 0,
                border: "none",
                background: "transparent",
                color: "inherit",
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              {label}
            </button>
          );
        },
      },
      {
        field: "description",
        headerName: t("collection.onlineDescription"),
        minWidth: 260,
        flex: 1,
      },
    ];
  }, [language, t]);

  const openAddAccount = () => {
    setAccountForm({
      account_holder_id: "",
      payment_method: "",
    });
    setAccountError("");
    setShowAddAccount(true);
  };

  const closeAddAccount = () => {
    if (!addingAccount) {
      setAccountError("");
      setShowAddAccount(false);
    }
  };

  const updateAccountForm = (field) => (event) => {
    setAccountForm((current) => ({
      ...current,
      [field]: event.target.value,
    }));
  };

  const submitAddAccount = async (event) => {
    event.preventDefault();
    setAddingAccount(true);
    setAccountError("");

    try {
      const authTokens = JSON.parse(
        localStorage.getItem("authTokens")
      );

      const response = await axios.post(
        `${API_URL}/accounting/online-payment-accounts/`,
        accountForm,
        {
          headers: {
            Authorization: `Bearer ${authTokens.access}`,
          },
        }
      );

      const createdAccount =
        response.data.online_account;

      setShowAddAccount(false);
      setAccountForm({
        account_holder_id: "",
        payment_method: "",
      });

      await loadAccountOptions();

      if (
        createdAccount &&
        createdAccount.online_account_id !== undefined
      ) {
        setPage(0);
        setSelectedOnlineAccountId(
          String(createdAccount.online_account_id)
        );
      }
    } catch (error) {
      setAccountError(
        errorMessage(
          error,
          language === "ar"
            ? "تعذر إضافة الحساب."
            : "Unable to add account."
        )
      );
    } finally {
      setAddingAccount(false);
    }
  };

  const openCashOut = async () => {
    if (!selectedAccount) {
      return;
    }

    const accountId = String(
      selectedAccount.online_account_id
    );

    setForm(emptyCashOut());
    setPaymentForm({
      ...emptyPayment(),
      online_account_id: accountId,
    });
    setModalTab("cash_out");
    setFormError("");
    setSuccess("");
    setCashOutOnlineAccountId(accountId);
    setCashOutReceipts([]);
    setSelectedCashOutIds([]);
    setLoadingCashOutReceipts(true);
    setShowCashOut(true);

    try {
      const authTokens = JSON.parse(
        localStorage.getItem("authTokens")
      );

      const response = await axios.get(
        `${API_URL}/accounting/online-drawer/`,
        {
          headers: {
            Authorization: `Bearer ${authTokens.access}`,
          },
          params: {
            online_account_id: accountId,
            cash_out_eligible: 1,
          },
        }
      );

      const returnedAccountId = String(
        response.data.online_account_id
      );

      if (returnedAccountId !== accountId) {
        throw new Error(
          "Online account response mismatch."
        );
      }

      setCashOutReceipts(
        Array.isArray(response.data.cash_out_receipts)
          ? response.data.cash_out_receipts
          : []
      );
    } catch (error) {
      setFormError(
        errorMessage(
          error,
          t("collection.onlineCashOutError")
        )
      );
    } finally {
      setLoadingCashOutReceipts(false);
    }
  };

  const closeCashOut = () => {
    if (!saving) {
      setFormError("");
      setShowCashOut(false);
      setCashOutOnlineAccountId("");
      setCashOutReceipts([]);
      setSelectedCashOutIds([]);
    }
  };

  const toggleCashOutReceipt = (collectionId) => {
    setSelectedCashOutIds((current) => {
      const exists = current.some(
        (value) =>
          String(value) === String(collectionId)
      );

      if (exists) {
        return current.filter(
          (value) =>
            String(value) !== String(collectionId)
        );
      }

      return [
        ...current,
        collectionId,
      ];
    });
  };

  const updateForm = (field) => (event) => {
    setForm((current) => ({
      ...current,
      [field]: event.target.value,
    }));
  };

  const updatePaymentForm = (field) => (event) => {
    setPaymentForm((current) => ({
      ...current,
      [field]: event.target.value,
    }));
  };

  const submitPayment = async (event) => {
    event.preventDefault();
    setSaving(true);
    setFormError("");

    try {
      const authTokens = JSON.parse(
        localStorage.getItem("authTokens")
      );

      await axios.post(
        `${API_URL}/accounting/online-drawer/`,
        {
          operation_id: paymentForm.operation_id,
          transaction_type: "payment",
          online_account_id: paymentForm.online_account_id,
          amount_usd: paymentForm.amount_usd || "0",
          amount_lbp: paymentForm.amount_lbp || "0",
          reason: paymentForm.reason,
        },
        {
          headers: {
            Authorization: `Bearer ${authTokens.access}`,
          },
        }
      );

      setShowCashOut(false);
      setPaymentForm(emptyPayment());
      setCashOutOnlineAccountId("");
      setCashOutReceipts([]);
      setSelectedCashOutIds([]);
      setSuccess(t("collection.onlinePaymentSuccess"));
      setPage(0);
      await loadDrawer(0, pageSize);
    } catch (error) {
      setFormError(
        errorMessage(error, t("collection.onlinePaymentError"))
      );
    } finally {
      setSaving(false);
    }
  };

  const submitCashOut = async (event) => {
    event.preventDefault();
    setSaving(true);
    setFormError("");

    try {
      const authTokens = JSON.parse(
        localStorage.getItem("authTokens")
      );

      await axios.post(
        `${API_URL}/accounting/online-drawer/`,
        {
          operation_id: form.operation_id,
          online_account_id: cashOutOnlineAccountId,
          collection_ids: selectedCashOutIds,
          currency: form.currency,
          fee: form.fee,
          reason: form.reason,
        },
        {
          headers: {
            Authorization: `Bearer ${authTokens.access}`,
          },
        }
      );

      setShowCashOut(false);
      setForm(emptyCashOut());
      setCashOutOnlineAccountId("");
      setCashOutReceipts([]);
      setSelectedCashOutIds([]);
      setSuccess(t("collection.onlineCashOutSuccess"));
      setPage(0);
      await loadDrawer(0, pageSize);
    } catch (error) {
      setFormError(
        errorMessage(error, t("collection.onlineCashOutError"))
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="online-payments-page" dir={direction}>
      <div className="online-payments-heading">
        <div className="online-account-overview">
          {selectedAccount && (
            <div className="online-balance-card">
              <span>{selectedAccount.label}</span>

              <strong>
                USD{" "}
                {formatMoney(
                  selectedAccount.balance_usd
                )}
              </strong>

              <strong>
                LBP{" "}
                {formatMoney(
                  selectedAccount.balance_lbp
                )}
              </strong>
            </div>
          )}

          <div
            className="online-account-tabs"
            aria-label="Online payment accounts"
          >
            {onlineAccounts.map((account) => {
              const selected =
                String(account.online_account_id) ===
                String(selectedOnlineAccountId);

              return (
                <button
                  key={account.online_account_id}
                  type="button"
                  className={
                    selected
                      ? "online-account-tab online-account-tab-active"
                      : "online-account-tab"
                  }
                  onClick={() => {
                    setPage(0);
                    setSelectedOnlineAccountId(
                      String(account.online_account_id)
                    );
                  }}
                >
                  {account.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="online-payments-actions">
          {canManageAccounts && (
            <button
              type="button"
              className="online-primary-button"
              onClick={openAddAccount}
            >
              {language === "ar"
                ? "إضافة حساب دفع إلكتروني"
                : "Add Online Payment Account"}
            </button>
          )}

          <button
            type="button"
            className="online-primary-button"
            onClick={openCashOut}
            disabled={!activeShift || !selectedAccount}
          >
            {t("collection.onlineCashOutPayments")}
          </button>
        </div>
      </div>

      {!loading && !activeShift && !pageError && (
        <div className="online-info-message">
          {t("collection.onlineNoShift")}
        </div>
      )}

      {success && (
        <div className="online-success-message">{success}</div>
      )}

      {pageError && (
        <div className="online-error-message">{pageError}</div>
      )}

      <div className="online-payments-grid-panel">
        <div className="online-payments-grid">
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.online_drawer_transaction_id}
            loading={loading}
            page={page}
            pageSize={pageSize}
            rowCount={rowCount}
            pagination
            paginationMode="server"
            rowsPerPageOptions={[25, 50, 100]}
            onPageChange={(nextPage) => setPage(nextPage)}
            onPageSizeChange={(nextSize) => {
              setPageSize(nextSize);
              setPage(0);
            }}
            disableSelectionOnClick
            localeText={{
              noRowsLabel: t("collection.onlineNoRows"),
            }}
          />
        </div>
      </div>

      <Modal
        show={showAddAccount}
        onHide={closeAddAccount}
        className="online-cashout-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {language === "ar"
              ? "إضافة حساب دفع إلكتروني"
              : "Add Online Payment Account"}
          </Modal.Title>
        </Modal.Header>

        <form onSubmit={submitAddAccount}>
          <Modal.Body>
            <div className="online-cashout-fields">
              <label>
                <span>
                  {language === "ar"
                    ? "صاحب الحساب"
                    : "Account Holder"}
                </span>

                <select
                  value={accountForm.account_holder_id}
                  onChange={updateAccountForm(
                    "account_holder_id"
                  )}
                  disabled={addingAccount}
                  required
                >
                  <option value="" disabled>
                    {language === "ar"
                      ? "اختر صاحب الحساب"
                      : "Select Account Holder"}
                  </option>

                  {accountHolders.map((holder) => (
                    <option
                      key={holder.account_holder_id}
                      value={String(
                        holder.account_holder_id
                      )}
                    >
                      {holder.label}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                <span>
                  {t("collection.onlinePaymentMethod")}
                </span>

                <select
                  value={accountForm.payment_method}
                  onChange={updateAccountForm(
                    "payment_method"
                  )}
                  disabled={addingAccount}
                  required
                >
                  <option value="" disabled>
                    {language === "ar"
                      ? "اختر طريقة الدفع"
                      : "Select Payment Method"}
                  </option>

                  {paymentMethods.map((method) => (
                    <option
                      key={method.value}
                      value={method.value}
                    >
                      {method.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {accountError && (
              <div className="online-error-message">
                {accountError}
              </div>
            )}
          </Modal.Body>

          <Modal.Footer>
            <button
              type="button"
              className="online-secondary-button"
              onClick={closeAddAccount}
              disabled={addingAccount}
            >
              {t("collection.onlineCancel")}
            </button>

            <button
              type="submit"
              className="online-primary-button"
              disabled={addingAccount}
            >
              {addingAccount
                ? t("collection.onlineSubmitting")
                : language === "ar"
                  ? "إضافة"
                  : "Add"}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      <Modal
        show={showCashOut}
        onHide={closeCashOut}
        className="online-cashout-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {modalTab === "payment"
              ? t("collection.onlinePaymentTitle")
              : t("collection.onlineCashOutTitle")}
          </Modal.Title>
        </Modal.Header>

        <div
          className="online-cashout-tabs"
          style={{ display: "flex", gap: "8px", padding: "12px 16px 0" }}
        >
          <button
            type="button"
            className={
              modalTab === "cash_out"
                ? "online-primary-button"
                : "online-secondary-button"
            }
            onClick={() => {
              setModalTab("cash_out");
              setFormError("");
            }}
            disabled={saving}
          >
            {t("collection.onlineCashOut")}
          </button>
          <button
            type="button"
            className={
              modalTab === "payment"
                ? "online-primary-button"
                : "online-secondary-button"
            }
            onClick={() => {
              setModalTab("payment");
              setFormError("");
            }}
            disabled={saving}
          >
            {t("collection.onlinePaymentTab")}
          </button>
        </div>

        {modalTab === "payment" ? (
        <form onSubmit={submitPayment}>
          <Modal.Body>
            <div className="online-cashout-fields">
              <label>
                <span>
                  {language === "ar"
                    ? "حساب الدفع الإلكتروني"
                    : "Online Account"}
                </span>

                <select
                  value={paymentForm.online_account_id}
                  onChange={updatePaymentForm("online_account_id")}
                  disabled={saving}
                  required
                >
                  {onlineAccounts.map((account) => (
                    <option
                      key={account.online_account_id}
                      value={String(account.online_account_id)}
                    >
                      {account.label}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                <span>{t("collection.onlineAmountUsd")}</span>

                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={paymentForm.amount_usd}
                  onChange={updatePaymentForm("amount_usd")}
                  onWheel={(event) => event.target.blur()}
                  disabled={saving}
                />
              </label>

              <label>
                <span>{t("collection.onlineAmountLbp")}</span>

                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={paymentForm.amount_lbp}
                  onChange={updatePaymentForm("amount_lbp")}
                  onWheel={(event) => event.target.blur()}
                  disabled={saving}
                />
              </label>
            </div>

            <label className="online-reason-field">
              <span>{t("collection.onlineDescription")}</span>
              <textarea
                value={paymentForm.reason}
                onChange={updatePaymentForm("reason")}
                disabled={saving}
                required
              />
            </label>

            {formError && (
              <div className="online-error-message">{formError}</div>
            )}
          </Modal.Body>

          <Modal.Footer>
            <button
              type="button"
              className="online-secondary-button"
              onClick={closeCashOut}
              disabled={saving}
            >
              {t("collection.onlineCancel")}
            </button>
            <button
              type="submit"
              className="online-primary-button"
              disabled={
                saving
                || !paymentForm.online_account_id
                || (
                  !Number(paymentForm.amount_usd)
                  && !Number(paymentForm.amount_lbp)
                )
                || !paymentForm.reason.trim()
              }
            >
              {saving
                ? t("collection.onlineSubmitting")
                : t("collection.onlinePaymentSubmit")}
            </button>
          </Modal.Footer>
        </form>
        ) : (
        <form onSubmit={submitCashOut}>
          <Modal.Body>
            <div className="online-cashout-account">
              <span>
                {language === "ar"
                  ? "حساب الدفع الإلكتروني"
                  : "Online Account"}
              </span>

              <strong>
                {cashOutAccount
                  ? cashOutAccount.label
                  : "-"}
              </strong>
            </div>

            <div className="online-cashout-receipts">
              <div className="online-cashout-receipts-title">
                {language === "ar"
                  ? "الإيصالات المتاحة للسحب"
                  : "Eligible Online Receipts"}
              </div>

              {loadingCashOutReceipts ? (
                <div className="online-cashout-empty">
                  {language === "ar"
                    ? "جارٍ تحميل الإيصالات..."
                    : "Loading receipts..."}
                </div>
              ) : cashOutReceipts.length === 0 ? (
                <div className="online-cashout-empty">
                  {language === "ar"
                    ? "لا توجد إيصالات متاحة للسحب."
                    : "No receipts are available for cash out."}
                </div>
              ) : (
                cashOutReceipts.map((row) => {
                  const checked = selectedCashOutIds.some(
                    (value) =>
                      String(value)
                      === String(
                        row.online_drawer_transaction_id
                      )
                  );

                  return (
                    <label
                      className="online-cashout-receipt"
                      key={
                        row.online_drawer_transaction_id
                      }
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          toggleCashOutReceipt(
                            row.online_drawer_transaction_id
                          )
                        }
                        disabled={saving}
                      />

                      <span>
                        Receipt #{row.receipt_id || "-"}
                        {" — "}
                        {row.client_name || "-"}
                      </span>

                      <strong>
                        USD{" "}
                        {formatMoney(
                          row.remaining_usd == null
                            ? row.amount_usd
                            : row.remaining_usd
                        )}
                        {" / "}
                        LBP{" "}
                        {formatMoney(
                          row.remaining_lbp == null
                            ? row.amount_lbp
                            : row.remaining_lbp
                        )}
                      </strong>
                    </label>
                  );
                })
              )}
            </div>

            <div className="online-cashout-fields">
              <label>
                <span>
                  {language === "ar"
                    ? "عملة الرسوم"
                    : "Fee Currency"}
                </span>

                <select
                  value={form.currency}
                  onChange={updateForm("currency")}
                  disabled={saving}
                  required
                >
                  <option value="USD">USD</option>
                  <option value="LBP">LBP</option>
                </select>
              </label>

              <label>
                <span>{t("collection.onlineFee")}</span>

                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.fee}
                  onChange={updateForm("fee")}
                  onWheel={(event) => event.target.blur()}
                  disabled={saving}
                  required
                />
              </label>
            </div>

            <label className="online-reason-field">
              <span>{t("collection.onlineReason")}</span>
              <textarea
                value={form.reason}
                onChange={updateForm("reason")}
                disabled={saving}
                required
              />
            </label>

            <div className="online-cashout-total">
              <span>
                {language === "ar"
                  ? "مبلغ السحب"
                  : "Cash Out Amount"}
              </span>

              <strong>
                USD{" "}
                {formatMoney(
                  selectedCashOutTotals.usd
                )}
                {" / "}
                LBP{" "}
                {formatMoney(
                  selectedCashOutTotals.lbp
                )}
              </strong>
            </div>

            {formError && (
              <div className="online-error-message">{formError}</div>
            )}
          </Modal.Body>

          <Modal.Footer>
            <button
              type="button"
              className="online-secondary-button"
              onClick={closeCashOut}
              disabled={saving}
            >
              {t("collection.onlineCancel")}
            </button>
            <button
              type="submit"
              className="online-primary-button"
              disabled={
                saving
                || loadingCashOutReceipts
                || selectedCashOutIds.length === 0
              }
            >
              {saving
                ? t("collection.onlineSubmitting")
                : t("collection.onlineSubmit")}
            </button>
          </Modal.Footer>
        </form>
        )}
      </Modal>
      <OnlineReceiptLinksModal
        show={linkedReceiptDetail.length > 0}
        onHide={() => setLinkedReceiptDetail([])}
        rows={linkedReceiptDetail}
        className="online-cashout-modal"
        isArabic={language === "ar"}
      />

    </div>
  );
}


export default OnlinePayments;
